/**
 * Admin Email Service
 * Handles sending emails via the admin API and fetching sent emails
 */

import pb from '../lib/pocketbase';

export interface SenderAlias {
  alias: string;
  email: string;
  name: string;
}

export interface SendEmailParams {
  sender_alias: string;
  to_email: string;
  subject: string;
  html_body: string;
  text_body?: string;
  sender_name?: string;
}

export interface SendEmailResponse {
  success: boolean;
  messageId?: string;
  recordId?: string;
  warning?: string;
  error?: string;
}

export interface SentEmail {
  id: string;
  sender_alias: string;
  sender_email: string;
  sender_name: string;
  to_email: string;
  subject: string;
  html_body: string;
  text_body: string;
  status: string;
  brevo_message_id: string;
  sent_by: string;
  sent_at: string;
  created: string;
  updated: string;
}

export interface SentEmailsFilter {
  search?: string;
  sender_alias?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
}

class AdminEmailService {
  /**
   * Get available sender aliases
   */
  async getSenderAliases(): Promise<SenderAlias[]> {
    try {
      // Check auth first
      if (!pb.authStore.isValid) {
        console.warn('Not authenticated, returning default aliases');
        return this.getDefaultAliases();
      }

      const response = await pb.send('/api/admin/email-aliases', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.aliases || [];
    } catch (error: any) {
      console.error('Failed to get sender aliases:', error);
      // Return default aliases on error
      return this.getDefaultAliases();
    }
  }

  /**
   * Get default sender aliases (fallback)
   */
  private getDefaultAliases(): SenderAlias[] {
    return [
      { alias: 'no-reply', email: 'no-reply@clickandclaim.eu', name: 'Click and Claim' },
      { alias: 'support', email: 'support@clickandclaim.eu', name: 'Click and Claim Support' },
      { alias: 'info', email: 'info@clickandclaim.eu', name: 'Click and Claim' },
      { alias: 'privacy', email: 'privacy@clickandclaim.eu', name: 'Click and Claim Privacy' },
      { alias: 'security', email: 'security@clickandclaim.eu', name: 'Click and Claim Security' },
      { alias: 'sales', email: 'sales@clickandclaim.eu', name: 'Click and Claim Sales' }
    ];
  }

  /**
   * Send an email via the admin API
   */
  async sendEmail(params: SendEmailParams): Promise<SendEmailResponse> {
    try {
      // Ensure we have a valid auth token
      if (!pb.authStore.isValid || !pb.authStore.token) {
        return {
          success: false,
          error: 'Not authenticated. Please log in again.'
        };
      }

      // Use fetch directly with explicit auth header to ensure token is sent
      const baseUrl = pb.baseUrl;
      const response = await fetch(`${baseUrl}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': pb.authStore.token
        },
        body: JSON.stringify(params)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || `Request failed with status ${response.status}`
        };
      }
      
      return data;
    } catch (error: any) {
      console.error('Failed to send email:', error);
      return {
        success: false,
        error: error?.message || 'Failed to send email'
      };
    }
  }

  /**
   * Get sent emails with filtering and pagination
   */
  async getSentEmails(filter: SentEmailsFilter = {}): Promise<{
    items: SentEmail[];
    totalItems: number;
    totalPages: number;
    page: number;
  }> {
    try {
      const page = filter.page || 1;
      const perPage = filter.perPage || 20;
      
      // Build filter string
      const filterParts: string[] = [];
      
      if (filter.search) {
        filterParts.push(`(to_email ~ "${filter.search}" || subject ~ "${filter.search}")`);
      }
      
      if (filter.sender_alias) {
        filterParts.push(`sender_alias = "${filter.sender_alias}"`);
      }
      
      if (filter.status) {
        filterParts.push(`status = "${filter.status}"`);
      }
      
      if (filter.dateFrom) {
        filterParts.push(`sent_at >= "${filter.dateFrom}"`);
      }
      
      if (filter.dateTo) {
        filterParts.push(`sent_at <= "${filter.dateTo}"`);
      }
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : '';
      
      const result = await pb.collection('sent_emails').getList<SentEmail>(page, perPage, {
        filter: filterString,
        sort: '-sent_at',
        expand: 'sent_by'
      });
      
      return {
        items: result.items,
        totalItems: result.totalItems,
        totalPages: result.totalPages,
        page: result.page
      };
    } catch (error: any) {
      console.error('Failed to get sent emails:', error);
      throw new Error(error?.message || 'Failed to get sent emails');
    }
  }

  /**
   * Get a single sent email by ID
   */
  async getSentEmail(id: string): Promise<SentEmail> {
    try {
      return await pb.collection('sent_emails').getOne<SentEmail>(id, {
        expand: 'sent_by'
      });
    } catch (error: any) {
      console.error('Failed to get sent email:', error);
      throw new Error(error?.message || 'Failed to get sent email');
    }
  }
}

export const adminEmailService = new AdminEmailService();
